<template>
  <AppWithLoading :fetch="fetchVacanciesWithUsers" v-slot:default="{ localLoading }">
    <VRow v-if="!localLoading">
      <VCol cols="12">
        <VTabs
          v-model="tab"
          background-color="transparent"
          color="dark"
          slider-size="4"
          slider-color="primary"
        >
          <VTab
            class="mr-6 mr-md-12"
            :class="[$vuetify.breakpoint.mdAndUp ? 'title' : 'body-2 font-weight-bold']"
          >
            {{ $t('myVacancies') }}
          </VTab>
          <VTab
            class="mr-6 mr-md-12"
            :class="[$vuetify.breakpoint.mdAndUp ? 'title' : 'body-2 font-weight-bold']"
          >
            {{ $t('archive') }}
          </VTab>
        </VTabs>
        <VTabsItems v-model="tab">
          <VTabItem>
            <div class="vacancies--actions mb-7">
              <VacancyStoreButton />
              <IntegrationsVacancyImportDialog />
              <!-- TODO: Раскомментировать когда будет релиз интеграции HH -->
              <!-- <VBtn
                v-if="shouldShowHHIntegration"
                color="primary"
                @click="goToHHPage"
                class="hh-import-btn-vacancies"
              >
                <span class="hh-import-btn-text-full">{{ $t('importVacanciesFromHH') }}</span>
                <span class="hh-import-btn-text-short">{{ $t('importVacanciesFromHHShort') }}</span>
                <img :src="require('@/assets/img/hh-logo.svg')" class="ml-2" />
              </VBtn> -->
            </div>
            <VacanciesTable
              @fetch="fetchVacancies($event)"
              @clearSelectedUsers="clearSelectedUsers"
              :company="company"
              :vacancies="vacancies"
              :selected-users="selectedUsers"
              :select-data="selectData"
            />
          </VTabItem>
          <VTabItem>
            <VacanciesArchiveTable
              @fetch="fetchVacancies($event)"
              @clearSelectedUsers="clearSelectedUsers"
              :company="company"
              :vacancies="archive"
              :selected-users="archiveSelectedUsers"
              :select-data="selectArchiveData"
            />
          </VTabItem>
        </VTabsItems>
      </VCol>
      <IntegrationsHelpDialogVacancies />
    </VRow>
    <VRow v-else-if="errorMessage">
      <VCol cols="12">
        <VAlert type="error">
          {{ errorMessage }}
        </VAlert>
      </VCol>
    </VRow>
  </AppWithLoading>
</template>

<script>
import IntegrationsVacancyImportDialog from '@/components/Integrations/IntegrationsVacancyImportDialog';
import IntegrationsHelpDialogVacancies from '@/components/Integrations/IntegrationsHelpDialogVacancies';
import useUser from '@/compositions/useUser';
import useHHIntegration from '@/compositions/useHHIntegration';
import AppWithLoading from '@/components/AppWithLoading.vue';
import DialogHeader from '@/components/DialogHeader';
import { paymentTabs, settingsTabs } from '@/enums';
import VacanciesTable from '../components/Vacancy/VacancyTable.vue';
import VacanciesArchiveTable from '../components/Vacancy/VacancyArchiveTable.vue';
import ValidationMixin from '../mixins/ValidationMixin';
import Vacancy from '../orm/models/Vacancy/Vacancy';
import VacancyStoreButton from '../components/Vacancy/VacancyStoreButton';
import { shouldShowHHIntegration } from '../helpers/constants';

export default {
  name: 'Vacancies',
  metaInfo() {
    return {
      title: this.$t('vacancies'),
    };
  },
  components: {
    AppWithLoading,
    IntegrationsHelpDialogVacancies,
    IntegrationsVacancyImportDialog,
    VacanciesTable,
    VacanciesArchiveTable,
    VacancyStoreButton,
    DialogHeader,
  },
  mixins: [ValidationMixin],
  props: {
    company: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { users, getUsers } = useUser();
    const hhIntegration = useHHIntegration();

    return {
      users,
      getUsers,
      hhIntegration,
      shouldShowHHIntegration: shouldShowHHIntegration(),
    };
  },
  data() {
    return {
      dialogImport: false,
      tabList: [
        'active',
        'archive',
        'integrations',
      ],
      selectedUsers: [],
      archiveSelectedUsers: [],
      waitSelect: false,
      timer: null,
      rules: {
        required: (value) => !!value || this.$t('required'),
      },
    };
  },
  computed: {
    paymentTabs() {
      return paymentTabs;
    },
    settingsTabs() {
      return settingsTabs;
    },
    vacancies() {
      if (this.company) {
        return Vacancy.scopes
          .notArchivedVacancies()
          .where('companyId', this.company.id)
          .with('address')
          .get();
      }

      return Vacancy.scopes
        .notArchivedVacancies()
        .with('address')
        .get();
    },
    archive() {
      return Vacancy.scopes.archivedVacancies().with('address').all();
    },
    tab: {
      get() {
        return this.tabList.indexOf(this.status) || 0;
      },
      async set(index) {
        await this.$router.replace({ name: 'vacancies', params: { status: this.tabList[index] } });
        this.selectedUsers = [];
        this.archiveSelectedUsers = [];
        await this.fetchVacanciesWithUsers();
      },
    },
    selectData() {
      if (!this.users) {
        return [];
      }

      return this.users.filter((user) => {
        return user.hasActiveVacancies;
      }).map((user) => {
        user.fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

        if (!user.position) {
          return user;
        }

        user.fullName = `${user.fullName}, ${user.position}`;

        return user;
      });
    },
    selectArchiveData() {
      if (!this.users) {
        return [];
      }

      return this.users.filter((user) => {
        return user.hasArchiveVacancies;
      }).map((user) => {
        user.fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

        if (!user.position) {
          return user;
        }

        user.fullName = `${user.fullName}, ${user.position}`;

        return user;
      });
    },
  },
  methods: {
    async fetchVacanciesWithUsers() {
      await this.withApiErrorHandling(async () => {
        await this.getUsers();
        await Vacancy.api().fetchAllByCompanyId(this.company.id);
      }, (e) => {
        this.errorMessage = e.message;
      });
    },
    async fetchVacancies(data = []) {
      if (this.tabList[this.tab] === 'active') {
        this.selectedUsers = data;
      } else {
        this.archiveSelectedUsers = data;
      }
      const selectedUsers = data;

      await this.withApiErrorHandling(async () => {
        if (selectedUsers.length > 0) {
          Vacancy.query().deleteAll();
          await Vacancy.api().fetchByCompanyIdAndUsers(
            this.company.id,
            { selectedUsers },
          );
        } else {
          await Vacancy.api().fetchAllByCompanyId(
            this.company.id,
          );
        }
      }, (e) => {
        this.errorMessage = e.message;
      });
    },
    clearSelectedUsers() {
      this.selectedUsers = [];
    },
    async goToHHPage() {
      await this.hhIntegration.goToHHImportPage(this.$router);
    },
  },
};
</script>

<style lang="scss" scoped>
.vacancies--actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  @media screen and (max-width: 385px) {
    margin-top: 30px;
  }
}

.hh-import-btn-vacancies {
  .hh-import-btn-text-short {
    display: none;
  }

  @media screen and (max-width: 350px) {
    .hh-import-btn-text-full {
      display: none;
    }

    .hh-import-btn-text-short {
      display: inline;
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  vacancies: Jobs
  myVacancies: My jobs
  archive: Archive
  integrations: Integrations
  selectUserLabel: Select user
  upgradePlan: Increase the tariff
  importVacanciesFromHH: Import vacancies from HeadHunter
  importVacanciesFromHHShort: Import vacancies from HH
  required: Required field
</i18n>

<i18n locale="ru" lang="yaml">
  vacancies: Вакансии
  myVacancies: Мои вакансии
  archive: Архив
  integrations: Интеграции
  selectUserLabel: Выберите пользователя
  upgradePlan: Повысить тариф
  importVacanciesFromHH: Импортировать вакансии из HeadHunter
  importVacanciesFromHHShort: Импортировать вакансии из HH
  required: Обязательное поле
</i18n>
